import { useMemo } from 'react';
import { useOutletContext, Link } from 'react-router-dom';
import {
  Clock, TrendingDown, AlertCircle, CheckCircle2, ArrowRight,
  PackageOpen, Mail, FileEdit, Sparkles, Users
} from 'lucide-react';
import PageHeader from '../../components/ui/PageHeader';
import Card, { CardHeader } from '../../components/ui/Card';
import Badge from '../../components/ui/Badge';
import StatusBadge from '../../components/ui/StatusBadge';
import Button from '../../components/ui/Button';
import Avatar from '../../components/ui/Avatar';
import Skeleton from '../../components/ui/Skeleton';
import { formatMoneyCompact, formatRelative } from '../../utils/formatters';
import { useShop } from '../../hooks/useShop';
import { useAuth } from '../../contexts/AuthContext';
import { usePartsToReturn, useTaskCounts, useTasks } from '../../hooks/usePartsToReturn';

export default function AuditorDashboardPage() {
  const { dashboard, loading } = useOutletContext();
  const { currentShop, currentShopId } = useShop();
  const { user } = useAuth();

  // Live data from new endpoints
  const { data: ptrData, loading: ptrLoading } = usePartsToReturn(currentShopId);
  const { counts: taskCounts } = useTaskCounts(currentShopId);
  const { tasks } = useTasks({ assigned_to: 'me', status: 'RESOLVED,IN_PROGRESS,ASSIGNED', limit: 10 });

  const stats = useMemo(() => {
    if (!dashboard) return null;
    const creditsCount = dashboard.credits?.count || 0;
    const creditsDollars = dashboard.credits?.total_outstanding_usd || 0;
    const missingCount = dashboard.missing_invoices?.count || 0;
    const missingDollars = dashboard.missing_invoices?.total_untracked_cost_usd || 0;
    const coresCount = dashboard.unreturned_cores?.count || 0;
    const coresDollars = dashboard.unreturned_cores?.total_core_at_risk_usd || 0;
    const costCount = dashboard.cost_discrepancies?.count || 0;
    const costDollars = dashboard.cost_discrepancies?.total_variance_usd || 0;
    const ptrDollars = ptrData?.total_at_risk_usd || 0;
    const totalExposure = creditsDollars + missingDollars + coresDollars + costDollars + ptrDollars;
    const totalIssues = creditsCount + missingCount + coresCount + costCount + (ptrData?.count || 0);
    return { creditsCount, creditsDollars, missingCount, missingDollars, coresCount, coresDollars, costCount, costDollars, ptrDollars, totalExposure, totalIssues };
  }, [dashboard, ptrData]);

  const shopLabel = currentShop?.nickname || currentShop?.name || 'Your shop';
  const firstName = (user?.full_name || 'Auditor').split(' ')[0];
  const today = new Date().toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' });

  return (
    <div>
      <PageHeader
        title={<>Good morning, <span className="text-[var(--color-recovery-600)]">{firstName}</span></>}
        description={`${today} · Today's audit across ${shopLabel}`}
        actions={
          <>
            <Button variant="ghost" leftIcon={<FileEdit size={16} />}>
              Draft email
            </Button>
            <Button variant="primary" leftIcon={<Sparkles size={16} />}>
              Run audit
            </Button>
          </>
        }
      />

      {/* Top metrics row */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6 stagger-children">
        <MetricTile
          label="Open Exposure"
          value={stats ? formatMoneyCompact(stats.totalExposure) : '—'}
          sub={stats ? `${stats.totalIssues} open items` : 'Loading…'}
          loading={loading}
          accent="warm"
          icon={<TrendingDown />}
        />
        <MetricTile
          label="Parts to Return"
          value={ptrData?.count ?? (ptrLoading ? null : 0)}
          sub={
            ptrData
              ? ptrData.overdue_count > 0
                ? `${ptrData.overdue_count} over ${ptrData.sla_days}-day SLA`
                : `${formatMoneyCompact(ptrData.total_at_risk_usd)} at risk`
              : 'Loading…'
          }
          loading={ptrLoading}
          accent={ptrData?.overdue_count > 0 ? 'danger' : 'action'}
          icon={<PackageOpen />}
          badge={ptrData?.overdue_count > 0 ? `${ptrData.overdue_count} overdue` : null}
        />
        <MetricTile
          label="Credits Outstanding"
          value={stats?.creditsCount ?? '—'}
          sub={stats ? formatMoneyCompact(stats.creditsDollars) : 'Loading…'}
          loading={loading}
          accent="recovery"
          icon={<RefreshIcon />}
        />
        <MetricTile
          label="Tasks Awaiting Verify"
          value={taskCounts?.awaiting_verification ?? 0}
          sub={`${taskCounts?.total ?? 0} total active`}
          accent="insight"
          icon={<CheckCircle2 />}
        />
      </div>

      {/* Main grid */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-6">
          <PartsToReturnCard
            items={ptrData?.items || []}
            slaDays={ptrData?.sla_days || 7}
            loading={ptrLoading}
          />
          <DailyEmailCard stats={stats} loading={loading} />
        </div>

        <div className="space-y-6">
          <TasksCard tasks={tasks} />
          <QuickActions />
        </div>
      </div>
    </div>
  );
}

// ─── Sub components ───

function MetricTile({ label, value, sub, loading, accent = 'warm', icon, badge }) {
  const accents = {
    warm:     'bg-[var(--color-cream-50)] border-[var(--color-warm-100)]',
    recovery: 'bg-[var(--color-recovery-50)] border-[var(--color-recovery-100)]',
    action:   'bg-[var(--color-action-50)] border-[var(--color-action-100)]',
    insight:  'bg-[var(--color-insight-50)] border-[var(--color-insight-100)]',
    danger:   'bg-[var(--color-danger-50)] border-[var(--color-danger-100)]',
    success:  'bg-[var(--color-success-50)] border-[var(--color-success-100)]',
  };
  const iconColors = {
    warm:     'text-[var(--color-warm-500)]',
    recovery: 'text-[var(--color-recovery-500)]',
    action:   'text-[var(--color-action-500)]',
    insight:  'text-[var(--color-insight-500)]',
    danger:   'text-[var(--color-danger-500)]',
    success:  'text-[var(--color-success-500)]',
  };

  return (
    <div className={`p-5 rounded-[var(--radius-lg)] border ${accents[accent]} shadow-[var(--shadow-xs)]`}>
      <div className="flex items-start justify-between mb-3">
        <div className={`w-9 h-9 rounded-[var(--radius-md)] bg-white/60 flex items-center justify-center ${iconColors[accent]} [&_svg]:w-[18px] [&_svg]:h-[18px]`}>
          {icon}
        </div>
        {badge && <Badge tone="danger" size="xs">{badge}</Badge>}
      </div>
      <div className="text-[11px] font-bold uppercase tracking-[0.8px] text-[var(--color-warm-500)] mb-1">
        {label}
      </div>
      {loading ? (
        <Skeleton height={28} className="w-2/3 mb-1" />
      ) : (
        <div className="text-[28px] font-extrabold text-[var(--color-warm-900)] tracking-tight tabular-nums leading-none mb-1">
          {value ?? '—'}
        </div>
      )}
      <div className="text-[12px] text-[var(--color-warm-600)] tabular-nums">{sub}</div>
    </div>
  );
}

function PartsToReturnCard({ items, slaDays, loading }) {
  const displayItems = items.slice(0, 8);

  return (
    <Card padded={false}>
      <div className="p-5 pb-3">
        <CardHeader
          title={
            <span className="flex items-center gap-2.5">
              <PackageOpen size={18} className="text-[var(--color-action-500)]" />
              Parts to Return Bucket
            </span>
          }
          description={`Received but not on an RO, awaiting vendor pickup. ${slaDays}-day SLA.`}
          action={
            <Button variant="ghost" size="sm" rightIcon={<ArrowRight size={14} />}>
              View all
            </Button>
          }
        />
      </div>

      {loading ? (
        <div className="p-5 pt-0 space-y-3">
          {[1, 2, 3].map((i) => (
            <Skeleton key={i} height={50} />
          ))}
        </div>
      ) : items.length === 0 ? (
        <div className="p-8 text-center">
          <CheckCircle2 size={32} className="text-[var(--color-success-500)] mx-auto mb-2" />
          <div className="text-[14px] font-semibold text-[var(--color-warm-900)]">
            Return bucket is clear
          </div>
          <div className="text-[12px] text-[var(--color-warm-500)] mt-1">
            All received parts are on ROs or already returned.
          </div>
        </div>
      ) : (
        <ul className="divide-y divide-[var(--color-warm-100)]">
          {displayItems.map((item) => {
            const isOverdue = item.is_overdue;
            const isWarning = !isOverdue && item.days_in_bucket >= slaDays - 2;
            return (
              <li key={item.state_id || item.purchase_order_part_id} className="px-5 py-3 hover:bg-[var(--color-cream-100)] transition-colors cursor-pointer">
                <div className="flex items-center gap-4">
                  <div className={`w-1 h-10 rounded-full shrink-0 ${
                    isOverdue ? 'bg-[var(--color-danger-500)]'
                    : isWarning ? 'bg-[var(--color-action-500)]'
                    : 'bg-[var(--color-success-500)]'
                  }`} />

                  <div className="min-w-0 flex-1">
                    <div className="text-[13px] font-semibold text-[var(--color-warm-900)] truncate">
                      {item.part_name || item.part_number || 'Unnamed part'}
                    </div>
                    <div className="text-[11px] text-[var(--color-warm-500)] mt-0.5 flex items-center gap-2">
                      <span className="mono-nums">{item.po_number || `PO-${item.purchase_order_id}`}</span>
                      <span className="text-[var(--color-warm-300)]">·</span>
                      <span>{item.vendor_name}</span>
                    </div>
                  </div>

                  <div className="text-right shrink-0">
                    <div className="text-[13px] font-semibold text-[var(--color-warm-900)] tabular-nums">
                      ${(item.line_total_usd || item.cost_usd || 0).toFixed(2)}
                    </div>
                    <div className={`text-[11px] font-bold tabular-nums mt-0.5 ${
                      isOverdue ? 'text-[var(--color-danger-600)]'
                      : isWarning ? 'text-[var(--color-action-600)]'
                      : 'text-[var(--color-success-600)]'
                    }`}>
                      {isOverdue && <Clock size={10} className="inline mr-0.5 -mt-0.5" />}
                      Day {item.days_in_bucket}/{slaDays}
                    </div>
                  </div>
                </div>
              </li>
            );
          })}
        </ul>
      )}
    </Card>
  );
}

function DailyEmailCard({ stats, loading }) {
  return (
    <Card padded={false}>
      <div className="p-5">
        <CardHeader
          title={
            <span className="flex items-center gap-2.5">
              <Mail size={18} className="text-[var(--color-insight-500)]" />
              Today's Audit Email
            </span>
          }
          description="Draft generated from today's audit. Review, edit notes, and send."
          action={
            <div className="flex gap-2">
              <Button variant="ghost" size="sm">Edit</Button>
              <Button variant="action" size="sm" leftIcon={<Mail size={13} />}>Send</Button>
            </div>
          }
        />

        <div className="text-[13px] text-[var(--color-warm-700)] leading-relaxed">
          {loading ? (
            <Skeleton height={14} className="w-2/3" />
          ) : (
            <>
              Today's audit flagged <span className="font-semibold text-[var(--color-warm-900)]">{stats?.totalIssues ?? 0}</span> items
              {stats?.totalExposure > 0 && (
                <>
                  {' '}totaling <span className="font-semibold text-[var(--color-warm-900)] tabular-nums">{formatMoneyCompact(stats.totalExposure)}</span>
                </>
              )}.
              Click <strong>Edit</strong> to review the draft and add your notes, then send.
            </>
          )}
        </div>
      </div>
    </Card>
  );
}

function TasksCard({ tasks }) {
  const toVerify = tasks.filter((t) => t.status === 'RESOLVED');
  return (
    <Card padded={false}>
      <div className="p-5">
        <CardHeader
          title={
            <span className="flex items-center gap-2.5">
              <Users size={18} className="text-[var(--color-recovery-500)]" />
              Tasks
            </span>
          }
          description={toVerify.length > 0 ? `${toVerify.length} awaiting your verification` : 'All clear'}
          action={
            <Button variant="ghost" size="sm" rightIcon={<ArrowRight size={14} />}>View</Button>
          }
        />
      </div>
      {tasks.length === 0 ? (
        <div className="px-5 pb-6 text-center">
          <CheckCircle2 size={28} className="text-[var(--color-success-500)] mx-auto mb-2" />
          <div className="text-[13px] font-semibold text-[var(--color-warm-900)]">No open tasks</div>
          <div className="text-[11px] text-[var(--color-warm-500)] mt-1">Flag issues from the audit to create tasks.</div>
        </div>
      ) : (
        <ul className="divide-y divide-[var(--color-warm-100)]">
          {tasks.slice(0, 5).map((task) => (
            <li key={task.id} className="px-5 py-3.5 hover:bg-[var(--color-cream-100)] transition-colors">
              <div className="flex items-start gap-3">
                <Avatar name={task.vendor_name || 'Task'} size="sm" />
                <div className="min-w-0 flex-1">
                  <div className="text-[13px] font-semibold text-[var(--color-warm-900)] leading-snug">
                    {task.title}
                  </div>
                  <div className="flex items-center gap-2 mt-1.5 flex-wrap">
                    <StatusBadge status={task.status.toLowerCase()} size="xs" />
                    <span className="text-[11px] text-[var(--color-warm-500)]">
                      {formatRelative(task.updated_at)}
                    </span>
                  </div>
                </div>
                {task.dollars_at_risk_usd != null && (
                  <div className="text-[12px] font-semibold text-[var(--color-warm-700)] tabular-nums shrink-0">
                    ${task.dollars_at_risk_usd.toFixed(0)}
                  </div>
                )}
              </div>
            </li>
          ))}
        </ul>
      )}
    </Card>
  );
}

function QuickActions() {
  return (
    <Card>
      <CardHeader
        title="Quick actions"
        description="Common auditor tasks"
      />
      <div className="space-y-2">
        <QuickAction
          icon={<AlertCircle size={16} />}
          title="Add exception rule"
          desc="Ignore a vendor or one-off"
          href="/auditor/exceptions/new"
        />
        <QuickAction
          icon={<FileEdit size={16} />}
          title="Upload statement"
          desc="Parse PDF from vendor"
          href="/auditor/statements"
        />
        <QuickAction
          icon={<CheckCircle2 size={16} />}
          title="Mark credits received"
          desc="Batch reconcile from statement"
          href="/auditor/credits"
        />
      </div>
    </Card>
  );
}

function QuickAction({ icon, title, desc, href }) {
  return (
    <Link
      to={href}
      className="flex items-center gap-3 p-3 -mx-2 rounded-[var(--radius-md)] hover:bg-[var(--color-cream-200)] transition-colors group"
    >
      <div className="w-9 h-9 rounded-[var(--radius-sm)] bg-[var(--color-cream-200)] group-hover:bg-[var(--color-cream-50)] flex items-center justify-center text-[var(--color-warm-700)] shrink-0 transition-colors">
        {icon}
      </div>
      <div className="min-w-0 flex-1">
        <div className="text-[13px] font-semibold text-[var(--color-warm-900)]">{title}</div>
        <div className="text-[11px] text-[var(--color-warm-500)]">{desc}</div>
      </div>
      <ArrowRight size={14} className="text-[var(--color-warm-400)] group-hover:text-[var(--color-warm-700)] group-hover:translate-x-0.5 transition-all shrink-0" />
    </Link>
  );
}

function RefreshIcon() {
  return <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M3 12a9 9 0 1 0 3-6.7L3 8"/><path d="M3 3v5h5"/></svg>;
}
